/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'react-feather';
import AuthService from '../../services/authService';
import AdminUserModal from './AdminUserModal';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Tier → refund points (mirrors agentClaimController pricing)
const TIER_POINTS = { flex: 10, bronze: 10, silver: 25, gold: 45, platinum: 65 };

const tierColors = (tier) => {
  switch ((tier || '').toLowerCase()) {
    case 'platinum': return { bg: 'rgba(168, 85, 247, 0.15)', fg: '#7c3aed' };
    case 'gold':     return { bg: 'rgba(234, 179, 8, 0.15)',  fg: '#a16207' };
    case 'silver':   return { bg: 'rgba(148, 163, 184, 0.2)', fg: '#475569' };
    case 'flex':     return { bg: 'rgba(209, 213, 219, 0.25)', fg: '#4b5563' };
    default:         return { bg: 'rgba(180, 83, 9, 0.15)',   fg: '#92400e' };
  }
};

const reasonLabel = (reason) => ({
  unresponsive: 'User is Unresponsive',
  cannot_service: 'Cannot Service User',
  other: 'Other'
}[reason] || reason);

const formatRelativeTime = (date) => {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

const ReturnRequestsPanel = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Approve / Reject modal state
  const [actionModal, setActionModal] = useState({ open: false, type: null, request: null });
  const [actionNotes, setActionNotes] = useState('');
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [actionError, setActionError] = useState('');

  // User detail modal — reuses the existing AdminUserModal
  const [adminUserModalOpen, setAdminUserModalOpen] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState(null);

  // Use a ref so polling reads the latest tab without recreating the interval
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const fetchRequests = useCallback(async (opts = {}) => {
    const { silent = false, tabOverride } = opts;
    if (!silent) setIsLoading(true);
    setError('');

    try {
      const tab = tabOverride || activeTabRef.current;
      const response = await fetch(`${API_URL}/return-requests?status=${tab}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (response.ok && data.success) {
        if (tab === activeTabRef.current) {
          setRequests(data.data || []);
        }
      } else {
        setError(data.message || 'Failed to load return requests');
      }
    } catch (err) {
      setError('Error loading return requests: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests({ tabOverride: activeTab });
  }, [activeTab, fetchRequests]);

  // 60s silent auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRequests({ silent: true });
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchRequests]);

  const openActionModal = (type, request) => {
    setActionModal({ open: true, type, request });
    setActionNotes('');
    setActionError('');
  };

  const closeActionModal = () => {
    setActionModal({ open: false, type: null, request: null });
    setActionNotes('');
    setActionError('');
  };

  const submitAction = async () => {
    if (!actionModal.request) return;
    if (actionModal.type === 'reject' && !actionNotes.trim()) {
      setActionError('A reason for rejection is required');
      return;
    }

    setIsSubmittingAction(true);
    setActionError('');

    try {
      const endpoint = actionModal.type === 'approve' ? 'approve' : 'reject';
      const response = await fetch(`${API_URL}/return-requests/${actionModal.request._id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ resolutionNotes: actionNotes.trim() || undefined })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const userName = actionModal.request.user?.name || 'user';
        const agentName = actionModal.request.agent?.name || 'agent';
        if (actionModal.type === 'approve') {
          const refunded = data.data?.refund?.refundedPoints;
          setSuccess(`Refunded ${userName} from ${agentName}${refunded != null ? ` (+${refunded} pts)` : ''}`);
        } else {
          setSuccess(`Rejected return request for ${userName}`);
        }
        setRequests(prev => prev.filter(r => r._id !== actionModal.request._id));
        closeActionModal();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setActionError(data.message || 'Action failed');
      }
    } catch (err) {
      setActionError('Error: ' + err.message);
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const openUserModal = (user) => {
    if (!user) return;
    setSelectedUserForModal(user);
    setAdminUserModalOpen(true);
  };

  const tierBadge = (tier) => {
    if (!tier) return null;
    const { bg, fg } = tierColors(tier);
    const pts = TIER_POINTS[tier.toLowerCase()] || 0;
    return (
      <span style={{
        padding: '3px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'capitalize',
        background: bg,
        color: fg,
        letterSpacing: '0.01em'
      }}>
        {tier} · {pts} pts
      </span>
    );
  };

  const renderRow = (request) => {
    const { _id, user, agent, reason, customReason, contactConfirmations, status, resolvedAt, resolvedBy, resolutionNotes, refundedPoints } = request;
    const isPending = status === 'pending';

    // Display reason inline:
    //  - 'unresponsive' → just say "User unresponsive" (+ a compact line of attempts if any)
    //  - 'cannot_service' → "Cannot service user"
    //  - 'other' → show the agent's typed text directly (no "Other" label)
    //  - fallback → show whatever text is there
    let reasonNode;
    if (reason === 'unresponsive') {
      const attempts = contactConfirmations
        ? Object.entries({ inApp: 'In-App', phone: 'Phone', email: 'Email' })
            .filter(([k]) => contactConfirmations[k])
            .map(([, label]) => label)
        : [];
      reasonNode = (
        <>
          <div style={{ color: '#111' }}>User unresponsive</div>
          {attempts.length > 0 && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
              Tried: {attempts.join(', ')}
            </div>
          )}
        </>
      );
    } else if (reason === 'cannot_service') {
      reasonNode = (
        <>
          <div style={{ color: '#111' }}>Cannot service user</div>
          {customReason && (
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {customReason}
            </div>
          )}
        </>
      );
    } else if (reason === 'other') {
      reasonNode = (
        <div style={{ color: '#111', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {customReason || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No reason provided</span>}
        </div>
      );
    } else {
      reasonNode = <div style={{ color: '#111' }}>{reasonLabel(reason)}</div>;
    }

    const userTier = user?.leadTier || request.leadTierSnapshot;

    return (
      <tr key={_id}>
        {/* User */}
        <td style={{ textAlign: 'left' }}>
          <div
            onClick={() => openUserModal(user)}
            style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600, display: 'inline-block' }}
            title="View user details"
          >
            {user?.name || 'Unknown'}
          </div>
          {user?.email && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
              {user.email}
            </div>
          )}
        </td>

        {/* Agent */}
        <td style={{ textAlign: 'left' }}>
          <div style={{ color: '#111', fontWeight: 600 }}>{agent?.name || 'Unknown'}</div>
          {(agent?.agentInfo?.agency || agent?.email) && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
              {agent?.agentInfo?.agency || agent?.email}
            </div>
          )}
        </td>

        {/* Tier */}
        <td style={{ textAlign: 'left' }}>
          {userTier ? tierBadge(userTier) : <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>}
        </td>

        {/* Reason — inline, no click-to-expand */}
        <td style={{ textAlign: 'left', fontSize: '13px' }}>
          {reasonNode}
          {!isPending && resolutionNotes && (
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
              Resolution: {resolutionNotes}
            </div>
          )}
        </td>

        {/* Action / Status */}
        <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          {isPending ? (
            <div style={{ display: 'inline-flex', gap: '6px' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => openActionModal('reject', request)}
              >
                Reject
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => openActionModal('approve', request)}
              >
                Approve
              </button>
            </div>
          ) : (
            <div>
              <span className={`rr-status-pill rr-status-${status}`}>
                {status === 'approved' && refundedPoints != null
                  ? `Approved · +${refundedPoints}`
                  : status}
              </span>
              {resolvedBy && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  by {resolvedBy.name} · {formatRelativeTime(resolvedAt)}
                </div>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="admin-panel" style={{
      color: '#111',
      minHeight: '100vh',
      background: '#fafafa',
      padding: '32px'
    }}>
      <style>{`
        /* Agent HQ — Control Center palette: #fafafa bg, #fff cards, #2563eb primary, #111 text */

        .admin-panel .alert-success {
          background-color: rgba(34, 197, 94, 0.08) !important;
          color: #166534 !important;
          border: 1px solid rgba(34, 197, 94, 0.25) !important;
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08) !important;
          color: #991b1b !important;
          border: 1px solid rgba(220, 38, 38, 0.25) !important;
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        /* Buttons */
        .admin-panel .btn {
          padding: 9px 18px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          color: #6b7280;
        }
        .admin-panel .btn:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #111;
        }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          color: #fff;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          transform: translateY(-1px);
        }
        .admin-panel .btn-secondary {
          background: #fff;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        }
        .admin-panel .btn-secondary:hover {
          background: #f9fafb;
          color: #111;
        }
        .admin-panel .btn:disabled {
          opacity: 0.6;
          cursor: default;
          transform: none;
        }

        /* Tabs — pill group matching Control Center */
        .admin-panel .tabs-container {
          display: inline-flex;
          background: rgba(26, 26, 26, 0.06);
          border-radius: 999px;
          padding: 3px;
          gap: 2px;
          margin-bottom: 24px;
          width: fit-content;
          border: none;
        }
        .admin-panel .tab-button {
          padding: 6px 16px;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.04em;
          cursor: pointer;
          border-radius: 999px;
          transition: all 0.15s;
          margin-bottom: 0;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          text-transform: none;
        }
        .admin-panel .tab-button:hover { color: #111; }
        .admin-panel .tab-button.active {
          color: #111;
          background: #fff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
        }

        @keyframes rr-status-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Modals */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(17, 17, 17, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }
        .modal-content {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border: none;
          width: 520px;
          max-width: 92vw;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
          background: #fff;
        }
        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.01em;
        }
        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }
        .modal-close:hover { background: #f3f4f6; color: #111; }
        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
          background: #fff;
        }
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          background: #fff;
        }

        /* Forms inside modal */
        .modal-body .form-group {
          margin-bottom: 16px;
        }
        .modal-body .form-label {
          display: block;
          color: #9ca3af;
          margin-bottom: 6px;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .modal-body .form-control {
          width: 100%;
          padding: 9px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          transition: all 0.15s;
          box-sizing: border-box;
          font-family: inherit;
          resize: vertical;
        }
        .modal-body .form-control:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .modal-body .form-control::placeholder { color: #9ca3af; }

        /* Loading + spinner (matches admin panel pattern) */
        .admin-panel .loading-state {
          text-align: center;
          padding: 48px 20px;
          color: #9ca3af;
          font-size: 13px;
        }
        .admin-panel .loading-state .spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #f0f0f0;
          border-top-color: #2563eb;
          border-radius: 50%;
          margin: 0 auto 12px;
          animation: rr-spin 0.8s linear infinite;
        }
        .admin-panel .empty-state {
          text-align: center;
          padding: 48px 20px;
          color: #9ca3af;
          font-size: 13px;
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .admin-panel .empty-state-title {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
        }

        @keyframes rr-spin { to { transform: rotate(360deg); } }

        /* ================= Tables ================= */
        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .admin-panel .dashboard-card .table-container {
          margin: 0 -20px -20px;
          overflow-x: auto;
          overflow-y: auto;
          max-height: 700px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 4px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
        .admin-panel .data-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          border-radius: 0;
          overflow: hidden;
          box-shadow: none;
          border: none;
          border-top: 1px solid #f0f0f0;
        }
        .admin-panel .data-table th {
          background: #fafafa;
          padding: 11px 14px;
          text-align: left;
          font-weight: 700;
          font-size: 10px;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #f0f0f0;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .admin-panel .data-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #f5f5f5;
          color: #111;
          font-size: 13px;
          vertical-align: middle;
        }
        .admin-panel .data-table tr:last-child td {
          border-bottom: none;
        }
        .admin-panel .data-table tr:hover {
          background: #fafafa;
        }

        /* Compact button variant for inline table actions */
        .admin-panel .btn-sm {
          padding: 6px 12px;
          font-size: 11px;
          border-radius: 8px;
        }

        /* Status pill — used inline in the action column for resolved rows */
        .rr-status-pill {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .rr-status-approved {
          background: rgba(34, 197, 94, 0.1);
          color: #15803d;
          border: 1px solid rgba(34, 197, 94, 0.25);
        }
        .rr-status-rejected {
          background: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
        }

        /* Action confirmation card inside modal */
        .rr-modal-summary {
          padding: 14px;
          background: #fafafa;
          border-radius: 10px;
          margin-bottom: 16px;
          border: 1px solid #f0f0f0;
        }
        .rr-modal-summary-name {
          font-weight: 700;
          color: #111;
          margin-bottom: 3px;
          font-size: 14px;
        }
        .rr-modal-summary-meta {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .rr-modal-warning {
          padding: 11px 13px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 12px;
          line-height: 1.5;
        }
        .rr-modal-warning-approve {
          background: rgba(234, 179, 8, 0.08);
          color: #92400e;
          border: 1px solid rgba(234, 179, 8, 0.2);
        }
        .rr-modal-warning-reject {
          background: rgba(220, 38, 38, 0.05);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.18);
        }
      `}</style>

      {/* Page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>User Returns</span>
      </div>

      {success && <div className="alert-success">{success}</div>}
      {error && <div className="alert-danger">{error}</div>}

      {/* Tabs */}
      <div className="tabs-container">
        {[
          { key: 'pending',  label: 'Pending',  dot: '#eab308', animated: true  },
          { key: 'approved', label: 'Approved', dot: '#10b981', animated: false },
          { key: 'rejected', label: 'Rejected', dot: '#9ca3af', animated: false }
        ].map(t => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              className={`tab-button ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {isActive && (
                <span style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  background: t.dot,
                  display: 'inline-block',
                  flexShrink: 0,
                  animation: t.animated ? 'rr-status-pulse 2.4s ease-in-out infinite' : 'none'
                }} />
              )}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          Loading return requests...
        </div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">
            {activeTab === 'pending' ? 'No pending requests' :
             activeTab === 'approved' ? 'No approved requests yet' : 'No rejected requests yet'}
          </div>
          <div>
            {activeTab === 'pending'
              ? "When agents submit return requests through their support modal, they'll show up here."
              : 'History will populate as you process pending requests.'}
          </div>
        </div>
      ) : (
        <div className="dashboard-card">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '180px', textAlign: 'left' }}>User</th>
                  <th style={{ minWidth: '180px', textAlign: 'left' }}>Agent</th>
                  <th style={{ minWidth: '110px', textAlign: 'left' }}>Tier</th>
                  <th style={{ minWidth: '220px', textAlign: 'left' }}>Reason</th>
                  <th style={{ textAlign: 'left' }}>{activeTab === 'pending' ? 'Action' : 'Status'}</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approve / Reject confirmation modal */}
      {actionModal.open && actionModal.request && (
        <div className="modal-overlay" onClick={closeActionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{actionModal.type === 'approve' ? 'Approve & Refund' : 'Reject Return Request'}</h3>
              <button className="modal-close" onClick={closeActionModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {actionError && <div className="alert-danger">{actionError}</div>}

              <div className="rr-modal-summary">
                <div className="rr-modal-summary-name">{actionModal.request.user?.name}</div>
                <div className="rr-modal-summary-meta">
                  Returning from {actionModal.request.agent?.name}
                </div>
                {tierBadge(actionModal.request.user?.leadTier || actionModal.request.leadTierSnapshot)}
              </div>

              {actionModal.type === 'approve' ? (
                <div className="rr-modal-warning rr-modal-warning-approve">
                  This will return the tier points to <strong>{actionModal.request.agent?.name}</strong>, remove this user from the agent, remove them from their pool, and set them back to available.
                </div>
              ) : (
                <div className="rr-modal-warning rr-modal-warning-reject">
                  Rejecting will leave the user assigned to <strong>{actionModal.request.agent?.name}</strong> with no point refund. The agent will not be automatically notified.
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  {actionModal.type === 'approve' ? 'Notes (Optional)' : 'Reason for Rejection *'}
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                  className="form-control"
                  placeholder={actionModal.type === 'approve'
                    ? 'Any internal notes about this approval...'
                    : 'Why is this request being rejected? (saved for audit)'}
                  style={{ minHeight: '72px' }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeActionModal}
                disabled={isSubmittingAction}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submitAction}
                disabled={isSubmittingAction}
              >
                {isSubmittingAction
                  ? 'Processing...'
                  : actionModal.type === 'approve' ? 'Confirm Refund' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User detail modal — reuses existing AdminUserModal */}
      {adminUserModalOpen && selectedUserForModal && (
        <AdminUserModal
          user={selectedUserForModal}
          onClose={() => {
            setAdminUserModalOpen(false);
            setSelectedUserForModal(null);
          }}
        />
      )}
    </div>
  );
};

export default ReturnRequestsPanel;